import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import * as dayjs from 'dayjs';
import 'dayjs/locale/az';
import 'dayjs/locale/en';
import 'dayjs/locale/ru';
import 'dayjs/locale/tr';
import translations from '../translations';

export type Language = 'az' | 'en' | 'ru' | 'tr';

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (key: string) => string;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

// Map language codes to dayjs locale codes
const dayjsLocaleMap: Record<Language, string> = {
  az: 'az',
  en: 'en',
  ru: 'ru',
  tr: 'tr',
};

export const LanguageProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [language, setLanguageState] = useState<Language>(() => {
    // Get language from localStorage or default to 'az'
    const savedLanguage = localStorage.getItem('language') as Language;
    return savedLanguage && ['az', 'en', 'ru', 'tr'].includes(savedLanguage) ? savedLanguage : 'az';
  });

  useEffect(() => {
    // Save language preference to localStorage
    localStorage.setItem('language', language);
    // Update dayjs locale
    dayjs.locale(dayjsLocaleMap[language]);
  }, [language]);

  const setLanguage = (lang: Language) => {
    setLanguageState(lang);
  };

  const t = (key: string): string => {
    const keys = key.split('.');
    let value: any = translations[language];
    
    for (const k of keys) {
      if (value && typeof value === 'object' && k in value) {
        value = value[k];
      } else {
        // Fallback to English if translation not found
        value = translations.en;
        for (const fallbackKey of keys) {
          if (value && typeof value === 'object' && fallbackKey in value) {
            value = value[fallbackKey];
          } else {
            return key; // Return key if translation not found
          }
        }
        break;
      }
    }
    
    return typeof value === 'string' ? value : key;
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

